import React from 'react';
import { Link } from 'react-router-dom';
import { Field } from '../../components/ui';
import { labelLines } from '../../utils/labelSheet';

// Label setup — the measurements on the left, one enlarged sticker in the
// middle (drag the text where you want it) and the whole A4 on the right.
export default function LabelSetupDesktop({ vm }) {
  const {
    tpl, change, setSheet, usePreset, presets, fields, block, picked, setPicked,
    setBlock, addBlock, removeBlock, startDrag, nudge, zoom, setZoom,
    problems, positions, perSheet, A4, printTest, reset, event,
  } = vm;

  const mmPerPx = 1 / zoom;
  const sample = { name: 'Ailani Kumar Uttamchand', subtitle: 'Sharma Traders', type: 'Family', city: 'Ulhasnagar', mobile: '9822210195', persons: 4, functions: ['Wedding', 'Reception'], extraCards: 0 };
  const lines = labelLines(sample, { eventName: event?.EventName }, tpl.blocks);
  const pageScale = 1.6;   // px per mm for the whole-sheet preview

  const num = (label, key, step = 0.1) => (
    <label className="field lbl-num">{label}
      <input type="number" step={step} value={tpl.sheet[key]} onChange={e => setSheet(key, e.target.value)} />
    </label>
  );

  return (
    <div>
      <div className="page-head">
        <h1>Label setup</h1>
        <span className="muted">{perSheet} labels per A4 sheet</span>
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn light" onClick={printTest} title="A numbered grid to check the printer's alignment">Test sheet</button>
        <button className="btn light" onClick={reset}>Reset</button>
        <Link className="btn primary" to="/app/labels">Print labels →</Link>
      </div>

      {problems.length > 0 && (
        <div className="card card-pad lbl-problems">
          {problems.map(p => <div key={p}>⚠ {p}</div>)}
        </div>
      )}

      <div className="lbl-layout">
        {/* ── the sheet's measurements ─────────────────────────────────── */}
        <div className="card card-pad">
          <div className="step-label">Sticker sheet</div>
          <Field label="Standard sheet" type="select" value={tpl.preset}
            options={presets.map(p => ({ value: p.key, label: p.label }))}
            onChange={usePreset} />
          <div className="lbl-grid">
            {num('Label width (mm)', 'labelWidth')}
            {num('Label height (mm)', 'labelHeight')}
            {num('Top margin (mm)', 'marginTop')}
            {num('Left margin (mm)', 'marginLeft')}
            {num('Gap across (mm)', 'gapX')}
            {num('Gap down (mm)', 'gapY')}
            {num('Columns', 'columns', 1)}
            {num('Rows', 'rows', 1)}
          </div>

          <div className="step-label" style={{ marginTop: 10 }}>Printer alignment</div>
          <div className="muted lbl-hint">
            Print the test sheet, lay a real label sheet over it, and type how far everything must move.
          </div>
          <div className="lbl-grid">
            <label className="field lbl-num">Shift right (mm)
              <input type="number" step="0.5" value={tpl.offsetX} onChange={e => change({ offsetX: Number(e.target.value) || 0 })} />
            </label>
            <label className="field lbl-num">Shift down (mm)
              <input type="number" step="0.5" value={tpl.offsetY} onChange={e => change({ offsetY: Number(e.target.value) || 0 })} />
            </label>
            <label className="field lbl-num">Inner padding (mm)
              <input type="number" step="0.5" value={tpl.padding} onChange={e => change({ padding: Number(e.target.value) || 0 })} />
            </label>
            <label className="field lbl-check">
              <input type="checkbox" checked={!!tpl.outline} onChange={e => change({ outline: e.target.checked })} />
              Print a thin outline round each label
            </label>
          </div>
        </div>

        {/* ── one sticker, as it will print ────────────────────────────── */}
        <div className="card card-pad">
          <div className="step-label">
            One label — drag the text where you want it
            <span className="muted" style={{ marginLeft: 8 }}>{tpl.sheet.labelWidth} × {tpl.sheet.labelHeight} mm</span>
          </div>
          <div className="lbl-zoom">
            <span className="muted">Zoom</span>
            <input type="range" min="3" max="12" step="1" value={zoom} onChange={e => setZoom(Number(e.target.value))} />
          </div>

          <div className="lbl-canvas-wrap">
            <div className="lbl-canvas" style={{ width: tpl.sheet.labelWidth * zoom, height: tpl.sheet.labelHeight * zoom }}
              onClick={(e) => { if (e.target.classList.contains('lbl-canvas')) setPicked(''); }}>
              {lines.map(b => (
                b.field === 'line' ? (
                  <div key={b.id} className={'lbl-block lbl-rule' + (picked === b.id ? ' on' : '')}
                    style={{ left: (tpl.padding + b.x) * zoom, top: (tpl.padding + b.y) * zoom, width: (b.w || 20) * zoom }}
                    onMouseDown={(e) => startDrag(b.id, e, mmPerPx)} />
                ) : (
                  <div key={b.id} className={'lbl-block' + (picked === b.id ? ' on' : '')}
                    style={{
                      left: (tpl.padding + b.x) * zoom, top: (tpl.padding + b.y) * zoom,
                      width: (b.w || 30) * zoom, fontSize: (b.size || 9) * zoom * 0.353,
                      fontWeight: b.bold ? 800 : 400, textAlign: b.align || 'left',
                    }}
                    onMouseDown={(e) => startDrag(b.id, e, mmPerPx)}>
                    {b.text}
                  </div>
                )
              ))}
            </div>
          </div>

          {/* what the chosen block does */}
          {block ? (
            <div className="lbl-block-edit" onKeyDown={(e) => {
              const step = e.shiftKey ? 5 : 1;
              if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-step, 0); }
              if (e.key === 'ArrowRight') { e.preventDefault(); nudge(step, 0); }
              if (e.key === 'ArrowUp') { e.preventDefault(); nudge(0, -step); }
              if (e.key === 'ArrowDown') { e.preventDefault(); nudge(0, step); }
            }} tabIndex={0}>
              <div className="lbl-grid">
                <Field label="Prints" type="select" value={block.field}
                  options={fields.map(f => ({ value: f.key, label: f.label }))}
                  onChange={(v) => setBlock({ field: v })} />
                {block.field === 'text' && (
                  <Field label="Text" value={block.text || ''} onChange={(v) => setBlock({ text: v })} />
                )}
                <label className="field lbl-num">Left (mm)
                  <input type="number" step="1" value={block.x} onChange={e => setBlock({ x: Number(e.target.value) || 0 })} />
                </label>
                <label className="field lbl-num">Top (mm)
                  <input type="number" step="1" value={block.y} onChange={e => setBlock({ y: Number(e.target.value) || 0 })} />
                </label>
                <label className="field lbl-num">Width (mm)
                  <input type="number" step="1" value={block.w} onChange={e => setBlock({ w: Number(e.target.value) || 0 })} />
                </label>
                <label className="field lbl-num">Font (pt)
                  <input type="number" step="0.5" value={block.size} onChange={e => setBlock({ size: Number(e.target.value) || 0 })} />
                </label>
                <Field label="Align" type="select" value={block.align || 'left'}
                  options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]}
                  onChange={(v) => setBlock({ align: v })} />
                <label className="field lbl-check">
                  <input type="checkbox" checked={!!block.bold} onChange={e => setBlock({ bold: e.target.checked })} />
                  Bold
                </label>
              </div>
              <div className="muted lbl-hint">Arrow keys nudge it a millimetre · Shift + arrow, five.</div>
              <button className="btn danger sm" onClick={() => removeBlock(block.id)}>Remove this line</button>
            </div>
          ) : (
            <div className="muted lbl-hint">Click a line on the label to change it.</div>
          )}

          <div className="step-label" style={{ marginTop: 10 }}>Add a line</div>
          <div className="chips-row">
            {fields.map(f => (
              <button key={f.key} type="button" className="chip-toggle" onClick={() => addBlock(f.key)}>+ {f.label}</button>
            ))}
          </div>
        </div>

        {/* ── the whole sheet ─────────────────────────────────────────── */}
        <div className="card card-pad">
          <div className="step-label">The A4 sheet</div>
          <div className="lbl-sheet" style={{ width: A4.width * pageScale, height: A4.height * pageScale }}>
            {positions.map((p, i) => (
              <div key={`${p.row}-${p.col}`} className="lbl-sheet-cell"
                style={{
                  left: p.x * pageScale, top: p.y * pageScale,
                  width: tpl.sheet.labelWidth * pageScale, height: tpl.sheet.labelHeight * pageScale,
                }}>
                <span>{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
